import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { startOfWeekYear, subDays } from 'date-fns'

import { Format, getCurrentPayPeriod, getPreviousPayPeriod, localizedStartOfWeek } from 'services/Dates'
import { MFilters } from 'types/filterTypes'

const useDateFilterOptions = (
  contentName: MFilters.ContentTypes,
  withTime?: boolean,
  hasPayPeriodsFilters?: boolean
): {
  getDateRange: (range: string) => { rangeStart: string | undefined; rangeEnd: string | undefined }
  dateOption: string
  setDateOption: Dispatch<SetStateAction<string>>
  datesOptions: string[]
} => {
  const today = useMemo(() => new Date(), [])
  const startOfWeek = useMemo(() => localizedStartOfWeek(today), [today])
  const filterKey: MFilters.FilterKey = `filter-${contentName}`
  const settings = useSelector((state: MStore.RootState) => state.settings)
  const savedOptions = useMemo(() => {
    return settings[filterKey] || {}
  }, [settings, filterKey])
  const { payPeriod, defaultBeginningOfWeekDay } = settings

  const baseDate = payPeriod?.baseDate || Format(startOfWeekYear(new Date(), { weekStartsOn: defaultBeginningOfWeekDay || 1 }), { outFormat: 'date' })
  const duration = payPeriod?.duration || 1
  let rangeStart = 'rangeStart' in savedOptions ? savedOptions.rangeStart : undefined
  let rangeEnd = 'rangeEnd' in savedOptions ? savedOptions.rangeEnd : undefined

  const [dateOption, setDateOption] = useState('')

  const [currentPayPeriodStart, currentPayPeriodEnd] = useMemo(() => getCurrentPayPeriod(baseDate, duration), [baseDate, duration])
  const [previousPayPeriodStart, previousPayPeriodEnd] = useMemo(() => getPreviousPayPeriod(currentPayPeriodStart, duration), [currentPayPeriodStart, duration])

  const datesOptions = hasPayPeriodsFilters
    ? ['TODAY', 'YESTERDAY', 'LAST_WEEK', 'CURRENT_PAY_PERIOD', 'PREVIOUS_PAY_PERIOD']
    : ['TODAY', 'LAST_SEVEN_DAYS', 'LAST_THIRTY_DAYS', 'LAST_365_DAYS']

  // If WithTime is set and rangeStart and rangeEnd are the same, just show the date
  const isRangeStartRangeEndSame = rangeStart === rangeEnd && withTime
  if (isRangeStartRangeEndSame) {
    rangeStart = rangeEnd = rangeStart?.slice(0, 10)
  }

  const getDateRange = (range: string) => {
    const dateRange = { rangeStart: '', rangeEnd: '' }
    switch (range) {
      case 'YESTERDAY': {
        dateRange['rangeStart'] = Format(subDays(today, 1), { outFormat: 'date' })
        dateRange['rangeEnd'] = Format(subDays(today, 1), { outFormat: 'date' })
        break
      }
      case 'TODAY': {
        dateRange['rangeStart'] = Format(today, { outFormat: 'date' })
        dateRange['rangeEnd'] = Format(today, { outFormat: 'date' })
        break
      }
      case 'CURRENT_PAY_PERIOD': {
        dateRange['rangeStart'] = currentPayPeriodStart
        dateRange['rangeEnd'] = currentPayPeriodEnd
        break
      }
      case 'PREVIOUS_PAY_PERIOD': {
        dateRange['rangeStart'] = previousPayPeriodStart
        dateRange['rangeEnd'] = previousPayPeriodEnd
        break
      }
      // This is the last week relative to the start day of the week of the company
      case 'LAST_WEEK': {
        dateRange['rangeStart'] = Format(subDays(startOfWeek, 7), { outFormat: 'date' })
        dateRange['rangeEnd'] = Format(subDays(startOfWeek, 1), { outFormat: 'date' })
        break
      }
      // This is the last 7 days relative to today
      case 'LAST_SEVEN_DAYS': {
        dateRange['rangeStart'] = Format(subDays(today, 7), { outFormat: 'date' })
        dateRange['rangeEnd'] = Format(today, { outFormat: 'date' })
        break
      }
      case 'LAST_THIRTY_DAYS': {
        dateRange['rangeStart'] = Format(subDays(today, 30), { outFormat: 'date' })
        dateRange['rangeEnd'] = Format(today, { outFormat: 'date' })
        break
      }
      case 'LAST_365_DAYS': {
        dateRange['rangeStart'] = Format(subDays(today, 365), { outFormat: 'date' })
        dateRange['rangeEnd'] = Format(today, { outFormat: 'date' })
        break
      }
      default: {
        break
      }
    }
    return dateRange
  }

  // Get the dateOption name from the rangeStart and rangeEnd to switch the right button in "active" state
  useEffect(() => {
    const getDateOptionFromDateRange = (rangeStart: string | undefined, rangeEnd: string | undefined) => {
      if (rangeStart === Format(today, { outFormat: 'date' }) && rangeEnd === Format(today, { outFormat: 'date' })) return 'TODAY'
      if (rangeStart === currentPayPeriodStart && rangeEnd === currentPayPeriodEnd) return 'CURRENT_PAY_PERIOD'
      if (rangeStart === previousPayPeriodStart && rangeEnd === previousPayPeriodEnd) return 'PREVIOUS_PAY_PERIOD'
      if (rangeStart === Format(subDays(today, 1), { outFormat: 'date' }) && rangeEnd === Format(subDays(today, 1), { outFormat: 'date' })) return 'YESTERDAY'
      if (rangeStart === Format(subDays(startOfWeek, 7), { outFormat: 'date' }) && rangeEnd === Format(subDays(startOfWeek, 1), { outFormat: 'date' }))
        return 'LAST_WEEK'
      let dateOption = 'CUSTOM'
      if (rangeEnd === Format(today, { outFormat: 'date' })) {
        switch (rangeStart) {
          case Format(subDays(today, 7), { outFormat: 'date' }): {
            dateOption = 'LAST_SEVEN_DAYS'
            break
          }
          case Format(subDays(today, 30), { outFormat: 'date' }): {
            dateOption = 'LAST_THIRTY_DAYS'
            break
          }
          case Format(subDays(today, 365), { outFormat: 'date' }): {
            dateOption = 'LAST_365_DAYS'
            break
          }
          default: {
            dateOption = 'CUSTOM'
            break
          }
        }
      }

      return dateOption
    }
    setDateOption(getDateOptionFromDateRange(rangeStart, rangeEnd))
  }, [rangeStart, rangeEnd, today, startOfWeek, currentPayPeriodEnd, currentPayPeriodStart, previousPayPeriodEnd, previousPayPeriodStart])

  return { getDateRange, dateOption, setDateOption, datesOptions }
}

export default useDateFilterOptions
